<template>
    <rkm-form
        ref="Form"
        v-if="model"
        show-legend
    >

        <rkm-area
            title="Identificação do paciente"
            type="fieldset"
        >
            <rkm-row>
                <rkm-col>
                    <h4>
                        {{ form.informacoesPaciente.nome }}
                        <strong v-if="form.informacoesPaciente.nomeSocial && form.informacoesPaciente.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            ({{ form.informacoesPaciente.nomeSocial }})
                        </strong>

                        <span
                            v-if="form.informacoesPaciente.responsavelFamiliar"
                            class="ml-1"
                            title="Responsável familiar"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="crown" />
                        </span>

                        <span
                            v-if="isTea"
                            class="ml-1"
                            title="Pessoa com Transtorno do Espectro Autista - TEA"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="infinity" />
                        </span>

                        <span
                            v-if="isPcd"
                            class="ml-1"
                            title="Pessoa com Deficiência - PCD"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="wheelchair" />
                        </span>
                    </h4>
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="form.informacoesPaciente.acompanhamentosSaude && form.informacoesPaciente.acompanhamentosSaude.length > 0">
                <rkm-col>
                    <div class="health-flags">
                        <span class="health-title">
                            Acompanhamentos:
                        </span>

                        <span
                            v-for="(acompanhamento, index) in form.informacoesPaciente.acompanhamentosSaude"
                            :key="index"
                            :class="`health-badge acompanhamento-${acompanhamento.value}`"
                        >
                            {{ acompanhamento.text }}
                        </span>
                    </div>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <p>
                        <strong>Prontuário:</strong>
                        {{ form.informacoesPaciente.prontuario }}
                    </p>
                    <p>
                        <strong>CPF:</strong>
                        {{ form.informacoesPaciente.cpf }}
                    </p>
                    <p>
                        <strong>CNS:</strong>
                        {{ form.informacoesPaciente.cns }}
                    </p>
                    <p>
                        <strong>RG:</strong>
                        {{ form.informacoesPaciente.rg || '-' }}
                    </p>
                    <p>
                        <strong>Telefone:</strong>
                        <template v-if="form.informacoesPaciente.telefoneNumero">
                            {{ form.informacoesPaciente.telefoneNumero | formatPhoneOrCellPhone }}
                        </template>
                        <template v-else>
                            -
                        </template>
                        <rkm-button
                            v-if="verificarPermissao('update-cadastro-paciente')"
                            @click="$refs.ModalTelefone.open(form.informacoesPaciente)"
                            class="ml-1"
                            color="primary"
                            title="Alterar telefone"
                            v-rkm-tooltip.hover
                            small
                        >
                            <rkm-icon icon="pen" />
                        </rkm-button>
                    </p>
                </rkm-col>

                <rkm-col cols="4">
                    <p>
                        <strong>Data de nascimento:</strong>
                        {{ form.informacoesPaciente.dataNascimento }}
                    </p>
                    <p>
                        <strong>Idade:</strong>
                        {{ form.informacoesPaciente.idade }}
                    </p>
                    <p>
                        <strong>Sexo:</strong>
                        {{ form.informacoesPaciente.sexo || '-' }}
                    </p>
                    <p>
                        <strong>Nome da mãe:</strong>
                        <template v-if="form.informacoesPaciente.nomeMae">
                            {{ form.informacoesPaciente.nomeMae }}
                        </template>
                        <template v-else>
                            <i>Desconhecido</i>
                        </template>
                    </p>
                </rkm-col>

                <rkm-col cols="4">
                    <p>
                        <strong>Classificação:</strong>
                        {{ classificacaoDescricao }}
                    </p>
                    <p>
                        <strong>Unidade de referência:</strong>
                        <template v-if="form.informacoesPaciente.unidadeReferencia">
                            {{ form.informacoesPaciente.unidadeReferencia }}
                        </template>
                        <template v-else>
                            <i>Não informado no cadastro do paciente</i>
                        </template>
                    </p>
                    <p>
                        <strong>Área e Microárea:</strong>
                        {{ form.informacoesPaciente.area || 'Não Informada' }} / {{ form.informacoesPaciente.microarea || 'Não Informada' }}
                    </p>
                    <p>
                        <strong>INE:</strong>
                        {{ form.informacoesPaciente.ine || 'Não Informado' }}
                    </p>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <p>
                        <strong>Endereço:</strong>
                        {{ form.informacoesPaciente.endereco || 'Não informado' }}
                    </p>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <ModalTelefone ref="ModalTelefone" />

        <rkm-area
            type="fieldset"
            title="Profissional responsável"
        >

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :repository="repositories.usuario"
                        :requestQueryStringParams="autocompleteRequestParams.usuario"
                        :searchFields="fields.autocomplete.usuario"
                        :validations="validations.usuario"
                        text-field="cidadaoNome"
                        label="Profissional"
                        placeholder="Digite o nome ou número do conselho profissional"
                        v-model="model.profissional"
                        uppercase
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-select
                        label="Unidade"
                        textField="nomeFantasia"
                        valueField="id"
                        v-model="model.unidade"
                        :options="form.unidades"
                        :validations="validations.unidade"
                        show-default-option
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-select
                        :options="form.infraestruturas"
                        label="Local de atendimento"
                        show-default-option
                        text-field="nome"
                        v-model="model.infraestrutura"
                        :validations="validations.infraestrutura"
                        default-option-text="Selecione"
                        value-field="id"
                        :disabled="model.unidade == null"
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-select
                        label="Código da equipe"
                        textField="descricao"
                        valueField="id"
                        v-model="model.equipe"
                        :options="form.equipes"
                        :validations="validations.equipe"
                        :disabled="model.infraestrutura == null"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        show-default-option
                        label="CBO"
                        textField="codigoNome"
                        valueField="id"
                        v-model="model.cbo"
                        :options="form.cbos"
                        :validations="validations.cbo"
                        :disabled="model.infraestrutura == null"
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-date-picker
                        label="Data"
                        :max-date="form.dataAtual"
                        v-model="model.dataAtendimento"
                        :validations="validations.dataAtendimento"
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-select
                        show-default-option
                        label="Tipo de local de atendimento"
                        text-field="descricao"
                        value-field="id"
                        :options="form.locaisAtendimento"
                        v-model="model.localAtendimento"
                        :validations="validations.localAtendimento"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area
            type="fieldset"
            :title="form.titulo"
            show-legend
        >
            <Questionario
                v-if="form.questionario.length >= 1"
                ref="questionario"
                :questionario="form.questionario"
                :titulo="form.tituloArea"
            />
        </rkm-area>
    </rkm-form>
</template>

<script>
// Mixins
import { repository, formats, data, stringCase, object, error, permissao as permissaoMixin } from '@/mixins'

// Components
import Questionario from './components/questionario/Form.vue'
import ModalTelefone from '@/components/ambulatorio/components/paciente/telefone/Modal'

// Filters.
import filters from '@/filters/formats'

// Vuex
import { mapActions } from 'vuex'

// Enums.
import permissao from '@/enums/atencao-basica/parametrizacao/permissao'
import localAtendimento from '@/enums/atencao-basica/marcador-consumo-alimentar/localAtendimento'
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

export default {
    mixins: [repository, formats, data, stringCase, object, error, permissaoMixin],
    components: {
        Questionario,
        ModalTelefone
    },
    filters: {
        formatPhoneOrCellPhone: filters.formatPhoneOrCellPhone,
    },
    props: {
        id: { type: Number, default: null }
    },
    data: () => ({
        form: {
            informacoesPaciente: [],
            dataAtual: null,
            titulo: '',
            tituloArea: '',
            locaisAtendimento: [],
            cbos: [],
            questionario: [],
            equipes: [],
            unidades: [],
            infraestruturas: [],
            autocompleteFields: {
                profissional: [
                    { key: 'nome', searchable: true },
                ]
            }
        },
        model: {
            cbo: null,
            dataAtendimento: null,
            equipe: null,
            localAtendimento: null,
            paciente: null,
            profissional: null,
            unidade: null,
            infraestrutura: null,
            questionario: null,
            usuario: null,
        },
        enums: {
            permissao,
            localAtendimento,
            preferenciaNome,
            classificacao,
            subclassificacao,
            deficienciaBase,
            deficiencia
        },
        unwatchers: {
            modelProfissional: null,
            modelUnidadeId: null,
        },
    }),
    computed: {
        classificacaoDescricao() {
            const paciente = this.form.informacoesPaciente

            if (paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO) {
                return 'Temporário'
            }

            if (paciente.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'Outro Município'
            }

            return 'Munícipe'
        },
        isTea() {
            const ids = this.form.informacoesPaciente.deficienciasBaseIds || []

            return ids.includes(this.enums.deficienciaBase.TEA)
                || this.form.informacoesPaciente.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        isPcd() {
            const ids = this.form.informacoesPaciente.deficienciasBaseIds || []

            return ids.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!this.form.informacoesPaciente.deficienciaId && this.form.informacoesPaciente.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        autocompleteRequestParams() {
            return {
                usuario: {
                    with_not_regularized: true,
                    with_atencao_basica_equipe: true,
                    atencao_basica_permissao: this.enums.permissao.MARCADOR_CONSUMO_ALIMENTAR
                },
            }
        },
        validations() {
            return {
                profissional: { required: true },
                cbo: { required: true },
                codigoEquipe: { required: true },
                unidade: { required: true },
                infraestrutura: { required: true },
                dataAtendimento: { required: true },
                localAtendimento: { required: true },
                equipe: { required: true }
            }
        },
        fields() {
            return {
                autocomplete: {
                    usuario: [
                        { key: "cadastroCidadao.nome", searchable: true },
                        { key: "cadastroUsuario.registroNumero", searchable: true },
                    ],
                    cid: [{ key: "nome", searchable: true }],
                },
            }
        },
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            if (this.id) {

                let url = this.$route.path.includes('create') ? 'create' : 'edit'

                const request = url == 'create'
                    ? this.repositories.marcadorConsumoAlimentar.create(this.id)
                    : this.repositories.marcadorConsumoAlimentar.edit(this.id)

                request
                    .then(({ data }) => {
                        this.form.informacoesPaciente = data.form.informacoesPaciente
                        this.form.titulo = data.form.titulo
                        this.form.tituloArea = data.form.tituloArea
                        this.form.dataAtual = data.form.maxDataAtual
                        this.form.locaisAtendimento = data.form.locaisAtendimento
                        this.form.questionario = data.form.perguntas
                        this.model.paciente = this.form.informacoesPaciente.id

                        this.model.localAtendimento = this.enums.localAtendimento.UBS

                        if (data.model) {

                            this.form.questionario.respostasMarcadas = data.model.perguntas

                            this.model.localAtendimento = data.model.localAtendimentoId
                            this.model.dataAtendimento = data.model.dataAtendimento
                            this.model.profissional = data.model.profissional

                            this.model.unidade = data.model.unidadeId
                            this.model.infraestrutura = data.model.infraestruturaId
                            this.model.equipe = data.model.equipeId
                            this.model.cbo = data.model.cboId

                       } else {

                            this.model.dataAtendimento = this.getFormatedDate(data.form.dataAtual, 'YYYY-MM-DD')
                       }

                        if (this.model.profissional && this.model.profissional.id) {

                            this.getUnidades()

                            if (this.model.unidade) {

                                this.getInfraestruturas()
                                this.getEquipes()
                                this.getOcupacoes()
                            }
                        }

                        this.$nextTick(function () {this.addWatchers()})
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        prepareData() {
            return this.camelObjectKeysToSnakeRecursive(Object.assign(this.clone(this.model), {
                telefone: {
                    id: this.form.informacoesPaciente.telefoneId,
                    numero: this.form.informacoesPaciente.telefoneNumero,
                    autorizaSms: this.form.informacoesPaciente.telefoneAutorizaSms,
                    atualizado: this.form.informacoesPaciente.telefoneAtualizado,
                },
            }))
        },
        save() {
            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    this.setLoading()

                    this.model.questionario = this.$refs.questionario.model

                    this.model.usuario = this.model.profissional.id

                    delete this.model.profissional

                    const url = this.$route.path.includes('create') ? 'create' : 'edit'

                    const request = url == 'create'
                        ? this.repositories.marcadorConsumoAlimentar.store(this.prepareData())
                        : this.repositories.marcadorConsumoAlimentar.update(this.id, this.prepareData())

                    request
                        .then(({ data }) => {
                            this.showSaveNotification()
                            this.$emit('save', data)
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        addWatchers() {

            this.addModelUnidadeIdWatcher()
            this.addModelProfissionalWatcher()
            this.addModelInfraestruturaIdWatcher()
        },
        addModelProfissionalWatcher() {

            this.unwatchers.modelProfissional = this.$watch('model.profissional', function (usuario) {

                if (usuario) {

                    this.getUnidades()
                }

                this.setCadastro('usuario')
            })
        },
        addModelUnidadeIdWatcher() {

            this.unwatchers.modelUnidadeId = this.$watch('model.unidade', function (unidadeId) {

                if (unidadeId) {

                    this.getInfraestruturas()
                }

                this.setCadastro('unidade')
            })
        },
        addModelInfraestruturaIdWatcher() {

            this.unwatchers.modelInfraestruturaId = this.$watch('model.infraestrutura', function (infraestruturaId) {

                if (infraestruturaId) {

                    this.getEquipes()
                    this.getOcupacoes()
                }

                this.setCadastro('infraestrutura')
            })
        },
        getUnidades() {

            this.setLoading()

            this.repositories.marcadorConsumoAlimentar.getUnidades({usuario_id: this.model.profissional.id})
                .then(({ data }) => {

                    this.form.unidades = data

                    if (data.length === 1){

                        this.model.unidade = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getInfraestruturas() {

            this.setLoading()

            this.repositories.marcadorConsumoAlimentar.getInfraestruturas({
                usuario_id: this.model.profissional.id,
                unidade_id: this.model.unidade
            })
                .then(({ data }) => {

                    this.form.infraestruturas = data

                    if (data.length === 1) {

                        this.model.infraestrutura = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getEquipes() {

            this.setLoading()

            this.repositories.marcadorConsumoAlimentar.getEquipes({
                usuario_id: this.model.profissional.id,
                infraestrutura_id: this.model.infraestrutura
            })
                .then(({ data }) => {

                    this.form.equipes = data

                    if (data.length === 1){

                        this.model.equipe = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getOcupacoes() {

            this.setLoading()

            this.repositories.marcadorConsumoAlimentar.getOcupacoes({
                usuario_id: this.model.profissional.id,
                infraestrutura_id: this.model.infraestrutura
            })
                .then(({ data }) => {

                    this.form.cbos = data

                    if (data.length === 1){

                        this.model.cbo = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        setCadastro(field) {

            if (field === 'usuario'){

                this.model.unidade = null
                this.model.infraestrutura = null
                this.model.equipe = null
                this.model.cbo = null
            } else if (field === 'unidade'){

                this.model.infraestrutura = null
                this.model.equipe = null
                this.model.cbo = null
            } else {

                this.model.equipe = null
                this.model.cbo = null
            }
        }
    },
    mounted() {
        this.model.questionario = this.$refs.questionario
    },
    created() {
        this.repositories = {
            marcadorConsumoAlimentar: this.getConectaSusRepository('atencaoBasica.marcadorConsumoAlimentar'),
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
        }
        this.load()
    },
}
</script>

<style scoped>
p {
    margin: 0px;
}

.health-flags {
    margin-top: 9px;
    padding-top: 2px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #e5e7eb;
}

.health-title {
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.health-badge {
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
}

.acompanhamento-idoso {
    color: #155724;
}

.acompanhamento-gestante {
    color: #ffb6c1;
}

.acompanhamento-hipertenso,
.acompanhamento-diabetico {
    color: #856404;
}

.acompanhamento-saude-mulher {
    color: #b34db2;
}

.acompanhamento-crescimento-desenvolvimento-infantil {
    color: #004085;
}
</style>